<!--
  PasswordChangeForm.vue
  Form for changing password (for users with existing password)

  Emits:
  - success: when password is changed successfully
  - cancel: when user cancels
-->

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useProfile } from '@/composables/useProfile'

const emit = defineEmits<{
  success: []
  cancel: []
}>()

const { changePassword, loading } = useProfile()

// Form state
const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const errorMessage = ref('')

// Validation
const passwordStrength = computed(() => {
  const pwd = newPassword.value
  if (!pwd) return { score: 0, label: '', color: '' }

  let score = 0
  if (pwd.length >= 8) score++
  if (/[A-Z]/.test(pwd)) score++
  if (/[a-z]/.test(pwd)) score++
  if (/\d/.test(pwd)) score++
  if (/[^A-Za-z0-9]/.test(pwd)) score++

  const labels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong']
  const colors = ['#ef4444', '#f59e0b', '#eab308', '#22c55e', '#10b981']

  return {
    score,
    label: labels[Math.min(score - 1, 4)] || '',
    color: colors[Math.min(score - 1, 4)] || '#ef4444',
  }
})

const passwordsMatch = computed(() => {
  if (!confirmPassword.value) return true
  return newPassword.value === confirmPassword.value
})

const isFormValid = computed(() => {
  return (
    currentPassword.value.length >= 1 &&
    newPassword.value.length >= 8 &&
    passwordStrength.value.score >= 3 &&
    passwordsMatch.value
  )
})

async function handleSubmit() {
  if (!isFormValid.value) return

  errorMessage.value = ''

  try {
    await changePassword(currentPassword.value, newPassword.value, confirmPassword.value)
    emit('success')
  } catch (err: unknown) {
    errorMessage.value = err instanceof Error ? err.message : 'Failed to change password'
  }
}
</script>

<template>
  <form class="password-form" @submit.prevent="handleSubmit">
    <div class="form-group">
      <label for="current-password">Current Password</label>
      <div class="password-input-wrapper">
        <input
          id="current-password"
          v-model="currentPassword"
          :type="showCurrentPassword ? 'text' : 'password'"
          required
          :disabled="loading"
          autocomplete="current-password"
          placeholder="Enter current password"
        />
        <button
          type="button"
          class="password-toggle"
          @click="showCurrentPassword = !showCurrentPassword"
          tabindex="-1"
        >
          <span class="material-symbols-outlined">
            {{ showCurrentPassword ? 'visibility_off' : 'visibility' }}
          </span>
        </button>
      </div>
    </div>

    <div class="form-group">
      <label for="new-password">New Password</label>
      <div class="password-input-wrapper">
        <input
          id="new-password"
          v-model="newPassword"
          :type="showNewPassword ? 'text' : 'password'"
          required
          :disabled="loading"
          autocomplete="new-password"
          minlength="8"
          placeholder="Enter new password"
        />
        <button
          type="button"
          class="password-toggle"
          @click="showNewPassword = !showNewPassword"
          tabindex="-1"
        >
          <span class="material-symbols-outlined">
            {{ showNewPassword ? 'visibility_off' : 'visibility' }}
          </span>
        </button>
      </div>
      <div v-if="newPassword" class="password-strength">
        <div class="strength-bar">
          <div
            class="strength-fill"
            :style="{
              width: `${(passwordStrength.score / 5) * 100}%`,
              backgroundColor: passwordStrength.color,
            }"
          ></div>
        </div>
        <span class="strength-label" :style="{ color: passwordStrength.color }">
          {{ passwordStrength.label }}
        </span>
      </div>
      <p class="password-requirements">
        Must have 8+ characters, uppercase, lowercase, and a number
      </p>
    </div>

    <div class="form-group">
      <label for="confirm-password">Confirm New Password</label>
      <input
        id="confirm-password"
        v-model="confirmPassword"
        type="password"
        required
        :disabled="loading"
        autocomplete="new-password"
        placeholder="Confirm new password"
      />
      <span v-if="!passwordsMatch" class="error-text">Passwords do not match</span>
    </div>

    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <div class="form-actions">
      <button type="button" class="btn-secondary" @click="emit('cancel')" :disabled="loading">
        Cancel
      </button>
      <button type="submit" class="btn-primary" :disabled="!isFormValid || loading">
        {{ loading ? 'Changing...' : 'Change Password' }}
      </button>
    </div>
  </form>
</template>

<style scoped>
.password-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-group label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.form-group input {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 0.625rem 0.875rem;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: rgba(212, 167, 44, 0.5);
  box-shadow: 0 0 0 3px rgba(212, 167, 44, 0.1);
}

.form-group input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper input {
  flex: 1;
  padding-right: 40px;
}

.password-toggle {
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);
  padding: 4px;
  display: flex;
  transition: color 0.2s;
}

.password-toggle:hover {
  color: rgba(255, 255, 255, 0.7);
}

.password-toggle .material-symbols-outlined {
  font-size: 1.125rem;
}

.password-strength {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.strength-bar {
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  transition: all 0.3s;
  border-radius: 2px;
}

.strength-label {
  font-size: 0.75rem;
  font-weight: 500;
  min-width: 70px;
}

.password-requirements {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
}

.error-text {
  font-size: 0.75rem;
  color: #ef4444;
}

.error-message {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #fca5a5;
  padding: 0.625rem 0.875rem;
  border-radius: 8px;
  font-size: 0.8125rem;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.btn-primary,
.btn-secondary {
  flex: 1;
  padding: 0.625rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #d4a72c 0%, #b58e1d 100%);
  border: none;
  color: #1a2f3f;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #e5b933 0%, #c99f2e 100%);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
}

.btn-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
